import { FC, PropsWithChildren, useEffect } from 'react'

import { IDomainEvent } from '@/utils/websocket/SocketService'
import { useSocket } from '@/utils/websocket/useSocket'

import { useActivityFeedWebsocketToken } from '../api/useActivityFeedWebsocketToken'
import { useOnFeedActivityUpdate } from '../api/useOnFeedActivityUpdate'
import { FeedItem } from '../domain/feed'

export type Entity = {
  id: string
  type: string
}

const ACTIVITY_FEED_SOCKET_NAMESPACE = '/shipment.activity-feed'

const useActivityFeedSocketEventListeners = (entities: Entity[]): IDomainEvent[] => {
  const onFeedActivityUpdate = useOnFeedActivityUpdate(entities)

  const publicDomainEventListeners = {
    eventType: 'ws-public-feed-activity-created',
    callback: (response: { payload: FeedItem }) => onFeedActivityUpdate(response.payload),
  }

  const eventListeners = [publicDomainEventListeners]

  return eventListeners as IDomainEvent[]
}

const useActivityFeedSocket = (entities: Entity[]) => {
  const eventListeners = useActivityFeedSocketEventListeners(entities)
  const {
    initializeSocket: initializeShipmentSocket,
    createDomainEventsListeners: createShipmentDomainEventsListeners,
    disconnectSocket: disconnectShipmentSocket,
  } = useSocket()
  const {
    initializeSocket: initializeBookingSocket,
    createDomainEventsListeners: createBookingDomainEventsListeners,
    disconnectSocket: disconnectBookingSocket,
  } = useSocket()

  const initialiseActivityFeedSocket = ({
    shipmentWebsocketToken,
    bookingWebsocketToken,
  }: {
    shipmentWebsocketToken: string | undefined
    bookingWebsocketToken: string | undefined
  }) => {
    entities.forEach((entity) => {
      /* Note: Now we have separate websockets for shipment and booking to support multiple entities for Activity Feed.
        But if there is a case to support multiple entities of the same type, (for example two shipment entities), this won't work as expected because the last entity's socket will override the previous one, and will miss the messages.
        It won't be a problem now because we only support one entity type at a time and it is most likely to stay that way.
        */
      // TODO(Masood): Once multiple-reference support is added to the generateWebsocketToken API, remove these separate socket initializations and unify the socket initialization
      if (shipmentWebsocketToken && entity.type === 'shipment') {
        initializeShipmentSocket({
          nameSpace: ACTIVITY_FEED_SOCKET_NAMESPACE,
          params: { referenceId: entity.id },
          token: shipmentWebsocketToken,
        })
        /* NOTE: Add domain event listeners for Shipment Websocket here, in case if there are multiple in future.
             It is easier to provide new config with callbacks */
        createShipmentDomainEventsListeners(eventListeners)
      } else if (bookingWebsocketToken && entity.type === 'booking') {
        initializeBookingSocket({
          nameSpace: ACTIVITY_FEED_SOCKET_NAMESPACE,
          params: { referenceId: entity.id },
          token: bookingWebsocketToken,
        })
        /* NOTE: Add domain event listeners for Booking Websocket here, in case if there are multiple in future.
          It is easier to provide new config with callbacks */
        createBookingDomainEventsListeners(eventListeners)
      }
    })
  }
  const disconnectSocket = () => {
    disconnectShipmentSocket()
    disconnectBookingSocket()
  }

  return {
    initialiseActivityFeedSocket,
    disconnectSocket,
  }
}

type ActivityFeedSocketProps = {
  entities: Entity[]
}

export const ActivityFeedSocket: FC<PropsWithChildren<ActivityFeedSocketProps>> = ({ entities, children }) => {
  const { initialiseActivityFeedSocket, disconnectSocket } = useActivityFeedSocket(entities)

  const { shipmentWebsocketToken, bookingWebsocketToken } = useActivityFeedWebsocketToken(entities)

  useEffect(() => {
    initialiseActivityFeedSocket({ shipmentWebsocketToken, bookingWebsocketToken })

    return () => {
      disconnectSocket()
    }
  }, [disconnectSocket, initialiseActivityFeedSocket, shipmentWebsocketToken, bookingWebsocketToken])

  return children
}
